import { Dispatch, SetStateAction } from 'react';
import { Spinner } from '@iwoca/orion';
import clsx from 'clsx';
import { NumberPicker } from 'components/NumberPicker/NumberPicker';
import { roundToStep } from 'components/NumberPicker/Stepper/Stepper.helpers';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import { lendingAPIPutWithdrawalFee } from 'lendingApi/lendingApi';
import {
  useGetAllowedWithdrawalFee,
  useGetWithdrawalFeeRate,
  useGetExpectedCommissions,
} from 'lendingApi/lendingApiHooks';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './SummaryCard.module.css';

export type TWithdrawalFeeOfferData = Partial<
  TIntroducerApplication['formal_offer_sets'][number]['formal_offers'][number]
> & {
  offer_id: string;
  product_type: any;
  withdrawal_fee_percentage: number;
  adjustable_fee: boolean;
};

type TWithdrawalFeeProps = {
  isDisabled: boolean;
  offer: TWithdrawalFeeOfferData;
  applicationId: string;
  withdrawalFee: number;
  offerIsSelected: boolean;
  offerNotSelected: boolean;
  isLoadingPayments: boolean;
  showExpectedCommissions: boolean;
  productName?: string;
  setIsLoadingPayments: Dispatch<SetStateAction<boolean>>;
  setWithdrawalFee: Dispatch<SetStateAction<number>>;
  isPartnerChannel: boolean;
};

export const WithdrawalFee = ({
  isDisabled,
  offer,
  applicationId,
  withdrawalFee,
  offerIsSelected,
  offerNotSelected,
  isLoadingPayments,
  showExpectedCommissions,
  setIsLoadingPayments,
  setWithdrawalFee,
  isPartnerChannel,
}: TWithdrawalFeeProps) => {
  const { isDE } = useLocale();
  const { trackEvent } = useAmplitude();
  const { formatPercentage } = useNumberFormat();
  const { introducerId, checkIntroducerPermission } = useIntroducerData();

  const { refetch: refetchWithdrawalFeeRate } = useGetWithdrawalFeeRate(
    offer.offer_id,
  );

  const { refetch: refetchExpectedCommission } = useGetExpectedCommissions(
    introducerId,
    applicationId,
    offer.offer_id,
    showExpectedCommissions,
  );

  const { data: allowedWithdrawalFees, isLoading: withdrawalFeeLoading } =
    useGetAllowedWithdrawalFee(
      introducerId as string,
      applicationId,
      offer.offer_id,
      offer.product_type,
    );

  if (!offer.adjustable_fee || isDisabled || isPartnerChannel || isDE) {
    return (
      <p
        className={clsx(styles.value, {
          [styles.selectedValue]: offerIsSelected,
          [styles.unselectedValue]: offerNotSelected,
        })}
      >
        {formatPercentage(offer.withdrawal_fee_percentage)}
      </p>
    );
  }

  if (withdrawalFeeLoading) {
    return (
      <div className={styles.loader}>
        <Spinner />
      </div>
    );
  }

  const handleCommissionChange = async (feeRate: number) => {
    const offerId = offer.offer_id;
    setWithdrawalFee(feeRate);

    trackEvent(AMPLITUDE_EVENTS.commissionFeeClicked, {
      'Withdrawal fee rate': feeRate,
    });

    await lendingAPIPutWithdrawalFee({
      introducerId: introducerId as string,
      applicationId,
      offerId,
      feeRate,
      setLoading: setIsLoadingPayments,
      // TODO need to discuss error states
      onError: () => console.log('error'),
    });
    refetchWithdrawalFeeRate();
    refetchExpectedCommission();
  };

  const allowedFeeRates = allowedWithdrawalFees?.data?.allowed_fee_rates ?? [];

  const options = allowedFeeRates
    .map(({ fee_rate, allowed }) => ({
      value: roundToStep(fee_rate * 100, 0.5),
      allowed,
    }))
    .sort((rateA, rateB) => rateA.value - rateB.value);

  const firstAllowed = options.find((option) => option.allowed);
  const lastAllowed = [...options].reverse().find((option) => option.allowed);

  const minPillValue = firstAllowed?.value;
  const maxPillValue = lastAllowed?.value;

  const formatMinMaxValues = (value: number) =>
    value % 1 === 0 ? value.toString() : value.toFixed(1);

  const pills: string[] = [
    ...(minPillValue && minPillValue !== 0
      ? [`Min ${formatMinMaxValues(minPillValue)}%`]
      : []),
    ...(maxPillValue !== undefined
      ? [`Max ${formatMinMaxValues(maxPillValue)}%`]
      : []),
  ];

  return (
    <div
      className={clsx(styles.withdrawalFeeContainer, {
        [styles.selectedValue]: offerIsSelected,
        [styles.unselectedValue]: offerNotSelected,
      })}
    >
      <NumberPicker
        id="withdrawal-fee"
        title="Withdrawal fee (%)"
        disabledStepper={isLoadingPayments}
        options={options}
        value={
          withdrawalFee !== null ? roundToStep(withdrawalFee * 100, 0.5) : null
        }
        pills={pills}
        disabled={!checkIntroducerPermission('can_interact_with_offers')}
        onChange={(val) => {
          handleCommissionChange(roundToStep(val / 100, 0.005));
        }}
      />
    </div>
  );
};
